import { S, player } from './state.js';
import { onMessage, OP, unpackPos } from './net.js';

// ============================================================
//  Звук: часть синтез через Web Audio, часть — реальные семплы из файлов
//  (/sounds/*.ogg, атрибуция в public/sounds/CREDITS.txt). Семплами играются
//  ШАГИ/БЕГ (CC0-пак Fantozzi), ВЫСТРЕЛЫ трёх стволов (CC0 The Free Firearm
//  Sound Library) и ПЕРЕЗАРЯДКА трёх стволов (три CC0-источника с
//  OpenGameArt). Причина одна на все случаи: синтез звучал жестянкой, а
//  через звук в шутере идёт половина информации — и шаг, и выстрел, и лязг
//  чужого магазина должны читаться ушами честно.
//
//  Две части:
//    • свои звуки (выстрел, перезарядка, шаги) — играются локально и сразу,
//      без ожидания сервера: задержка в полпинга на собственном выстреле
//      ощущается как «оружие залипает»;
//    • чужие звуки — приходят кадром OP_SOUND и играются ПОЗИЦИОННО.
//      Их порождает сервер (см. shared/src/sound.rs), поэтому заглушить
//      свои шаги для противника правкой клиента нельзя.
// ============================================================

let actx = null, noiseBuf = null;

export function initAudio() {
  if (actx) return;
  actx = new (window.AudioContext || window.webkitAudioContext)();
  const len = actx.sampleRate * 0.4; noiseBuf = actx.createBuffer(1, len, actx.sampleRate);
  const d = noiseBuf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  loadStepSamples();   // асинхронно; до загрузки шаг падает на синтез-фолбэк
  loadShotSamples();   // то же и для выстрела
  loadReloadSamples(); // и для перезарядки (свой звук и чужой — один файл)
  loadFxSamples();     // гранаты: бросок, взрывы, шипение дыма, горение
}

/**
 * Виды звуков — зеркало `shared/src/sound.rs`, менять только парой.
 * Разъедутся — выстрел будет звучать шагом, а поймать это можно только ушами.
 *
 * ⚠️ Объявлены ЗДЕСЬ, до всех потребителей: по ним индексируются таблицы
 * семплов, громкостей и дальностей, а часть из них читается своим выстрелом,
 * то есть кодом выше по файлу.
 */
// ⚠️ Номер 4 — бывшая обобщённая перезарядка (протокол ≤ 14), не занимать.
const SND_SHOT_PISTOL = 0, SND_SHOT_RIFLE = 1, SND_SHOT_SNIPER = 2,
  SND_STEP = 3, SND_LAND = 5, SND_STEP_RUN = 6,
  SND_RELOAD_PISTOL = 7, SND_RELOAD_RIFLE = 8, SND_RELOAD_SNIPER = 9;

/** Один файл → AudioBuffer. `null`, если файла нет или он не декодировался. */
async function loadSample(name) {
  try {
    const r = await fetch(`/sounds/${name}`);
    return await actx.decodeAudioData(await r.arrayBuffer());
  } catch {
    return null;   // молча: у каждого семпла есть синтез-фолбэк
  }
}

// ============================================================
//  Семплы шагов (реальные файлы). Грузятся один раз при initAudio.
//  Один семпл на шаг И на бег: бег — тот же звук громче и ниже по тону
//  (playbackRate < 1), читается как более тяжёлый топот. Пока не загрузились
//  — playStepSample падает на прежний синтез, звук просто беднее.
// ============================================================

const STEP_FILES = ['step1.ogg', 'step2.ogg', 'step3.ogg', 'step4.ogg', 'step5.ogg', 'step6.ogg'];
let stepBufs = [];
let lastStep = -1;   // чтобы один и тот же семпл не повторялся подряд

async function loadStepSamples() {
  stepBufs = (await Promise.all(STEP_FILES.map(loadSample))).filter(Boolean);
}

// ============================================================
//  Семплы выстрелов (реальные записи, CC0). Один файл на ствол, они же
//  играются и за себя, и за чужого — тот же принцип, что у шагов: свой
//  и чужой выстрел обязаны быть одним звуком, иначе по чужому нельзя
//  научиться узнавать оружие.
// ============================================================

const SHOT_FILES = {
  [SND_SHOT_PISTOL]: 'shot_pistol.ogg',
  [SND_SHOT_RIFLE]: 'shot_rifle.ogg',
  [SND_SHOT_SNIPER]: 'shot_sniper.ogg',
};
const shotBufs = {};   // вид звука → AudioBuffer

/**
 * Свой ствол → вид звука. ⚠️ Зеркало `sound.rs::shot_kind`: там ровно та же
 * таблица и ровно по той же причине — совпадение нумерации оружия и звуков
 * сейчас случайно, и завязываться на него молча не стоит.
 */
const SHOT_KIND = { pistol: SND_SHOT_PISTOL, rifle: SND_SHOT_RIFLE, sniper: SND_SHOT_SNIPER };

/**
 * Громкость выстрела по стволу (семплы сведены к одному пиковому уровню,
 * поэтому «мощность» ствола задаётся здесь, а не в файле).
 *
 * Одна таблица на свой и на чужой выстрел: свой играется как есть, чужой
 * дополнительно делится на затухание по дистанции, то есть чужой в упор
 * звучит как свой. Потолок держим ниже единицы намеренно — у винтовки
 * `rof = 0.10 с`, и хвосты 5-6 выстрелов складываются в один момент.
 */
const SHOT_GAIN = {
  [SND_SHOT_PISTOL]: 0.55, [SND_SHOT_RIFLE]: 0.50, [SND_SHOT_SNIPER]: 0.80,
};

async function loadShotSamples() {
  await Promise.all(Object.keys(SHOT_FILES).map(async (kind) => {
    const buf = await loadSample(SHOT_FILES[kind]);
    if (buf) shotBufs[kind] = buf;
  }));
}

// ============================================================
//  Семплы перезарядки (реальные записи, CC0 — источники в CREDITS.txt).
//  Свой и чужой звук — ОДИН файл, как у выстрела и шагов: иначе по чужой
//  перезарядке нельзя научиться узнавать, ЧТО перезаряжает противник,
//  а это и есть вся ценность звука («снайперка — он глухой 2.4 секунды»).
//
//  Каждый файл короче своей перезарядки в weapons.js (0.85 / 1.28 / 1.95 с
//  против 1.1 / 1.5 / 2.4 с) — намеренно: звук, который тянется дольше,
//  чем длится сама перезарядка, врёт слушателю о моменте готовности.
// ============================================================

const RELOAD_FILES = {
  [SND_RELOAD_PISTOL]: 'reload_pistol.ogg',
  [SND_RELOAD_RIFLE]: 'reload_rifle.ogg',
  [SND_RELOAD_SNIPER]: 'reload_sniper.ogg',
};
const reloadBufs = {};

/** Свой ствол → вид звука. ⚠️ Зеркало `sound.rs::reload_kind`. */
const RELOAD_KIND = {
  pistol: SND_RELOAD_PISTOL, rifle: SND_RELOAD_RIFLE, sniper: SND_RELOAD_SNIPER,
};

/**
 * Громкость перезарядки по стволу. Семплы нормализованы к одному пику,
 * поэтому разница задаётся здесь, а не в файлах (как SHOT_GAIN).
 * Заметно тише выстрела: это возня руками, а не хлопок.
 */
const RELOAD_GAIN = {
  [SND_RELOAD_PISTOL]: 0.30, [SND_RELOAD_RIFLE]: 0.30, [SND_RELOAD_SNIPER]: 0.34,
};

async function loadReloadSamples() {
  await Promise.all(Object.keys(RELOAD_FILES).map(async (kind) => {
    const buf = await loadSample(RELOAD_FILES[kind]);
    if (buf) reloadBufs[kind] = buf;
  }));
}

// ---- Обрыв перезарядки ----
//
// Перезарядку теперь можно ПРЕРВАТЬ (побежал — руки вынуты из магазина,
// см. weapons.js и room.rs). Значит звук обязан обрываться вместе с ней:
// семпл длится почти всю перезарядку, и доигранный до конца он говорит
// слушателю «противник вот-вот снова опасен» тогда, когда тот на самом деле
// побежал с пустым магазином. Информация ценнее звука — гасим.
//
// Своя перезарядка одна, поэтому ручка на неё одна. Чужих может звучать
// несколько сразу, и опознать «чью гасить» по кадру нельзя: в OP_SOUND нет
// id источника (это античит, см. shared/src/sound.rs). Поэтому чужие
// хранятся списком с ТОЧКОЙ, и гасится ближайшая к тому, кто перестал
// перезаряжаться (`stopReloadNear` зовёт remote.js, увидев погасший бит
// HELD_RELOAD при взведённом HELD_SPRINT).
let ownReload = null;          // {g, src} своей перезарядки
let ownReloadFallback = null;  // id таймера синтез-фолбэка (второй щелчок)
const farReloads = [];         // чужие: {g, src, x, y, z, until}

/** Плавно погасить источник за 60 мс. Резкий stop() даёт щелчок громче семпла. */
function fadeOut(snd) {
  if (!actx || !snd) return;
  const t = actx.currentTime;
  try {
    snd.g.gain.cancelScheduledValues(t);
    snd.g.gain.setValueAtTime(snd.g.gain.value, t);
    snd.g.gain.linearRampToValueAtTime(0, t + 0.06);
    snd.src.stop(t + 0.08);
  } catch { /* источник уже остановлен */ }
}

/** Оборвать СВОЮ перезарядку (её отменили бегом или сменой оружия). */
export function stopReload() {
  fadeOut(ownReload);
  ownReload = null;
  clearTimeout(ownReloadFallback);
  ownReloadFallback = null;
}

/**
 * Оборвать ЧУЖУЮ перезарядку, звучащую рядом с точкой.
 *
 * Радиус щедрый: позиция звука округлена сервером до сетки 0.5 м, а сам
 * игрок за время до отмены успевает шагнуть. Промахнуться не страшно —
 * лишний доигравший семпл лучше, чем оборванный чужой звук.
 */
export function stopReloadNear(x, y, z, r = 6) {
  let best = -1, bestD = r * r;
  for (let i = 0; i < farReloads.length; i++) {
    const s = farReloads[i];
    const dx = s.x - x, dy = s.y - y, dz = s.z - z;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 <= bestD) { bestD = d2; best = i; }
  }
  if (best < 0) return false;
  fadeOut(farReloads[best]);
  farReloads.splice(best, 1);
  return true;
}

/** Убрать из списка чужие перезарядки, которые уже доиграли сами. */
function sweepReloads() {
  const now = actx.currentTime;
  for (let i = farReloads.length - 1; i >= 0; i--) {
    if (farReloads[i].until <= now) farReloads.splice(i, 1);
  }
}

/**
 * Сыграть перезарядку из семпла. `false` — семпл ещё не загружен, зовущий
 * падает на прежний синтез (два щелчка), как и у выстрела.
 *
 * ⚠️ Высота тона здесь НЕ гуляет (в отличие от playBuffer у выстрела):
 * перезарядка — это механика конкретного ствола, и «расстроенный» лязг
 * затвора мешал бы узнавать оружие на слух, а очередями она не идёт.
 *
 * `at` — точка мира для чужой перезарядки (у своей её нет): по ней потом
 * ищется, какой источник гасить при обрыве.
 */
function playReloadSample(kind, gain, out, att = 1, at = null) {
  if (!actx || !reloadBufs[kind]) return false;
  const src = actx.createBufferSource();
  src.buffer = reloadBufs[kind];

  const g = actx.createGain();
  g.gain.value = gain;

  let tail = src;
  if (att < 0.98) {
    const flt = actx.createBiquadFilter();
    flt.type = 'lowpass';
    flt.frequency.value = 700 + 19000 * att * att;
    src.connect(flt); tail = flt;
  }
  tail.connect(g); g.connect(out || actx.destination);
  src.start();

  if (at) {
    sweepReloads();
    farReloads.push({
      g, src, x: at[0], y: at[1], z: at[2],
      until: actx.currentTime + src.buffer.duration,
    });
  } else {
    stopReload();                 // своя перезарядка одна: старая уступает новой
    ownReload = { g, src };
  }
  return true;
}

/**
 * Проиграть готовый буфер один раз.
 *
 * `att` — доля громкости от дистанции (1 = в упор, свои звуки). По ней
 * ставится ФНЧ: далёкий звук не просто тише, он ГЛУШЕ, иначе он слышен
 * как близкий, которому убавили громкость. У синтеза ту же роль играл
 * параметр `freq` у `noise()`, семплу нужен отдельный фильтр.
 *
 * Высота тона гуляет на ±3%: семпл на каждое событие один, и без разброса
 * очередь из винтовки (и серия взрывов) слышна как повтор одного файла.
 */
function playBuffer(buf, gain, out, att = 1) {
  const src = actx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = 0.97 + Math.random() * 0.06;

  const g = actx.createGain();
  g.gain.value = gain;

  let tail = src;
  if (att < 0.98) {
    const flt = actx.createBiquadFilter();
    flt.type = 'lowpass';
    // В упор — выше слышимого диапазона (фильтр не мешает), на пределе
    // слышимости — глухой бум за стеной.
    flt.frequency.value = 700 + 19000 * att * att;
    src.connect(flt); tail = flt;
  }
  tail.connect(g); g.connect(out || actx.destination);
  src.start();
}

/**
 * Сыграть выстрел из семпла. Возвращает `false`, если семпл ещё не загружен —
 * тогда зовущий падает на прежний синтез (как у шагов).
 */
function playShotSample(kind, gain, out, att = 1) {
  if (!actx || !shotBufs[kind]) return false;
  playBuffer(shotBufs[kind], gain, out, att);
  return true;
}

/**
 * Сыграть шаг/бег из семпла. `out` — узел назначения: у своих шагов колонки
 * напрямую (null), у чужих — цепочка «дистанция + панорама» из spatialOut.
 * `run` = бег: громче и ниже по тону. Пока семплы не подгрузились — синтез.
 */
function playStepSample(gain, out, run) {
  if (!actx) return;
  if (stepBufs.length) {
    let i = (Math.random() * stepBufs.length) | 0;
    if (i === lastStep && stepBufs.length > 1) i = (i + 1) % stepBufs.length;
    lastStep = i;
    const src = actx.createBufferSource();
    src.buffer = stepBufs[i];
    src.playbackRate.value = run ? 0.85 : 1.0;
    const g = actx.createGain(); g.gain.value = gain * (run ? 1.4 : 1.0);
    src.connect(g); g.connect(out || actx.destination);
    src.start();
  } else {
    noise(run ? 0.08 : 0.06, run ? 300 : 350, gain * (run ? 0.55 : 0.4), 'lowpass', out);
  }
}
export function resumeAudio() {
  if (actx && actx.state === 'suspended') actx.resume();
}

// `out` — узел назначения. По умолчанию колонки напрямую (свои звуки),
// для чужих туда подставляется цепочка «громкость по дистанции + панорама».
export function noise(dur, freq, gain, type = 'lowpass', out = null) {
  if (!actx) return;
  const src = actx.createBufferSource(); src.buffer = noiseBuf;
  const flt = actx.createBiquadFilter(); flt.type = type; flt.frequency.value = freq;
  const g = actx.createGain(); g.gain.setValueAtTime(gain, actx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, actx.currentTime + dur);
  src.connect(flt); flt.connect(g); g.connect(out || actx.destination);
  src.start(); src.stop(actx.currentTime + dur);
}
export function tone(freq, dur, gain, type = 'square', out = null) {
  if (!actx) return;
  const o = actx.createOscillator(); o.type = type; o.frequency.value = freq;
  const g = actx.createGain(); g.gain.setValueAtTime(gain, actx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, actx.currentTime + dur);
  o.connect(g); g.connect(out || actx.destination); o.start(); o.stop(actx.currentTime + dur);
}

// Свой выстрел — из семпла, локально и сразу (ждать эха с сервера нельзя:
// оно придёт через полпинга вторым щелчком). Пока файл не подгрузился —
// прежний синтез.
export const playShot = () => {
  const kind = SHOT_KIND[S.curKey];
  if (playShotSample(kind, SHOT_GAIN[kind], null)) return;
  const w = S.curKey;
  if (w === 'sniper') { noise(0.35, 900, 0.7); tone(70, 0.3, 0.4, 'sawtooth'); }
  else if (w === 'pistol') { noise(0.12, 1400, 0.4); tone(160, 0.08, 0.2); }
  else { noise(0.09, 1800, 0.35); tone(200, 0.05, 0.15); }
};
// Своя перезарядка — из семпла своего ствола, локально и сразу (как выстрел).
// Пока файл не подгрузился — прежний синтез из двух щелчков.
export const playReload = () => {
  const kind = RELOAD_KIND[S.curKey];
  if (playReloadSample(kind, RELOAD_GAIN[kind], null)) return;
  // Фолбэк-синтез тоже должен уметь обрываться: второй щелчок висит на
  // таймере, и без ссылки на него отменённая перезарядка всё равно щёлкнула бы.
  stopReload();
  tone(300, 0.05, 0.2, 'square');
  ownReloadFallback = setTimeout(() => tone(180, 0.08, 0.25), 180);
};
export const playHit = (head) => tone(head ? 1200 : 800, 0.05, 0.25, 'triangle');
export const playSwitch = () => tone(500, 0.04, 0.15, 'square');
// Свой шаг/бег — из семпла, локально и сразу. `run` приходит из
// player.js::movementEffects (mv.sprint && mv.moving — зеркало is_sprinting).
// ⚠️ База 0.175 (не 0.35): по просьбе владельца свои шаги тише вдвое — их
// слышишь постоянно, и на прежней громкости они забивали кадр. Бег внутри
// playStepSample = база ×1.4, поэтому и ходьба, и бег стали тише ровно на 50%.
// Чужие шаги (0.35 в playAt) уменьшены отдельной правкой и по той же причине.
export const playStep = (run = false) => playStepSample(0.175, null, run);
export const playEnemyShot = () => noise(0.1, 700, 0.12);
export const playHurt = () => { tone(90, 0.2, 0.35, 'sawtooth'); noise(0.15, 500, 0.2); };

// ============================================================
//  Позиционный звук (чужие источники)
// ============================================================

/**
 * «Опорная дистанция» затухания по видам, м.
 *
 * Громкость считается как ref/(ref+d): на расстоянии ref звук вдвое тише,
 * дальше спадает плавно и никогда не обрывается ступенькой. Отсечение по
 * дальности делает сервер (за пределом слышимости кадр просто не приходит),
 * поэтому здесь НЕ копия серверных радиусов, а параметр ощущения — его можно
 * крутить свободно, ничего не рассинхронизируя.
 */
const REF_DIST = {
  [SND_SHOT_PISTOL]: 16, [SND_SHOT_RIFLE]: 18, [SND_SHOT_SNIPER]: 26,
  [SND_STEP]: 5, [SND_STEP_RUN]: 6, [SND_LAND]: 6,
  [SND_RELOAD_PISTOL]: 5, [SND_RELOAD_RIFLE]: 5, [SND_RELOAD_SNIPER]: 5,
};

/**
 * Узел для звука взрыва в точке мира (для grenades.js::boomFx).
 *
 * Взрывы приходят кадром OP_BOOM и раньше игрались одинаково громко хоть
 * под ногами, хоть на другом конце карты — по ним нельзя было понять, куда
 * прилетело. `ref` большой: взрыв слышно почти отовсюду, но направление и
 * удалённость теперь читаются.
 *
 * ⚠️ Порог тишины здесь снят (`cutoff = 0`) намеренно. `noise`/`tone`
 * трактуют `null` как «играй прямо в колонки», поэтому вернуть `null`
 * из-за дальности значило бы дать дальнему взрыву ПОЛНУЮ громкость —
 * ровно наоборот задуманному. `null` отсюда возможен только когда звука
 * нет вовсе (контекст не создан), а тогда `noise`/`tone` и так молчат.
 */
export function boomOut(x, y, z, ref = 30) {
  if (!actx) return null;
  const out = spatialOut(x, y, z, ref, 0);
  return out && out.node;
}

/** Ближе этого расстояния панораму не считаем: у самого уха она дёргается */
const PAN_MIN_DIST = 0.8;

/**
 * Собрать цепочку «громкость → панорама → колонки» под конкретную точку.
 *
 * Полноценный PannerNode с 3D-листенером здесь не нужен: он требует
 * обновления ориентации слушателя каждый кадр и своей модели затухания,
 * а нам хватает скалярного произведения на вектор «вправо». Дёшево,
 * предсказуемо и в духе остального звука в проекте (чистый синтез).
 */
function spatialOut(x, y, z, ref, cutoff = 0.02) {
  const dx = x - player.pos.x, dy = y - player.pos.y, dz = z - player.pos.z;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const att = ref / (ref + dist);
  if (att < cutoff) return null;   // тише этого всё равно не слышно

  const g = actx.createGain();
  g.gain.value = att;

  // Вектор «вправо» от игрока. Вперёд — (−sin yaw, −cos yaw), как в физике
  // (см. player.js::stepPhysics), значит вправо — (cos yaw, −sin yaw).
  if (dist > PAN_MIN_DIST && actx.createStereoPanner) {
    const rx = Math.cos(S.yaw), rz = -Math.sin(S.yaw);
    const pan = Math.max(-1, Math.min(1, (dx * rx + dz * rz) / dist));
    const p = actx.createStereoPanner();
    p.pan.value = pan;
    g.connect(p); p.connect(actx.destination);
  } else {
    g.connect(actx.destination);
  }
  return { node: g, att };
}

/**
 * Сыграть чужой звук в точке мира.
 *
 * Дальние звуки не только тише, но и глуше: высокие частоты в воздухе
 * съедаются первыми, и без этого далёкий выстрел звучит как близкий,
 * которому просто убавили громкость.
 */
function playAt(kind, x, y, z) {
  if (!actx) return;
  const ref = REF_DIST[kind];
  if (ref === undefined) return;   // вид из будущей версии протокола — молчим
  const out = spatialOut(x, y, z, ref);
  if (!out) return;
  const { node, att } = out;
  const muffle = 0.35 + 0.65 * att;   // 1.0 вплотную → 0.35 на пределе слышимости

  // Выстрел — тот же семпл, что играет себе стрелявший, только через
  // позиционный узел и с ФНЧ по дистанции. Не загрузился — синтез ниже.
  if (kind === SND_SHOT_PISTOL || kind === SND_SHOT_RIFLE || kind === SND_SHOT_SNIPER) {
    if (playShotSample(kind, SHOT_GAIN[kind], node, att)) return;
  }

  if (kind === SND_SHOT_SNIPER) {
    noise(0.35, 900 * muffle, 0.7, 'lowpass', node);
    tone(70, 0.3, 0.4, 'sawtooth', node);
  } else if (kind === SND_SHOT_PISTOL) {
    noise(0.12, 1400 * muffle, 0.45, 'lowpass', node);
    tone(160, 0.08, 0.2, 'square', node);
  } else if (kind === SND_SHOT_RIFLE) {
    noise(0.09, 1800 * muffle, 0.4, 'lowpass', node);
    tone(200, 0.05, 0.18, 'square', node);
  } else if (kind === SND_STEP || kind === SND_STEP_RUN) {
    // Чужой шаг/бег — из того же семпла, что и свой, через позиционный узел.
    // ⚠️ База 0.35 (была 0.7): по просьбе владельца чужие шаги тише вдвое —
    // на прежней громкости идущий рядом бот перекрикивал выстрелы. Информация
    // при этом не потеряна: отсечение по дальности делает сервер, а не эта
    // цифра, то есть слышно ровно тех же и оттуда же, просто не в упор по ушам.
    playStepSample(0.35, node, kind === SND_STEP_RUN);
  } else if (kind === SND_LAND) {
    noise(0.09, 220, 0.45, 'lowpass', node);
  } else if (kind === SND_RELOAD_PISTOL || kind === SND_RELOAD_RIFLE || kind === SND_RELOAD_SNIPER) {
    // Тот же файл, что играет себе перезаряжающийся, — иначе по чужой
    // перезарядке нельзя научиться узнавать ствол. Громкость чужой берём
    // выше своей: она слышна только вплотную (RANGE_RELOAD = 14 м) и уже
    // поделена на затухание по дистанции.
    // Точку запоминаем: по ней потом гасится звук, если перезарядку прервали
    // бегом (id источника в кадре нет — см. shared/src/sound.rs).
    if (playReloadSample(kind, RELOAD_GAIN[kind] * 2.2, node, att, [x, y, z])) return;
    // Фолбэк — прежние два щелчка: по ним опознаётся хотя бы «он без патронов»
    tone(300, 0.05, 0.5, 'square', node);
    setTimeout(() => tone(180, 0.08, 0.6, 'square', node), 180);
  }
}

// ============================================================
//  Гранаты: бросок, взрывы по типам и ДЛЯЩИЕСЯ звуки
//
//  Всё это клиент играет САМ, без участия `OP_SOUND`, и это осознанно:
//  и взрыв (`OP_BOOM`), и летящая граната (`OP_NADE`) уже приходят всем
//  без PVS-culling — граната считается публичным объектом (см. room.rs).
//  Значит клиент и так знает тип, точку и время события, а отдельный вид
//  звука в кадре был бы второй копией той же информации.
// ============================================================

const FX_FILES = {
  throw: 'nade_throw.ogg',      // свист броска
  frag: 'boom_frag.ogg',        // осколочная
  flash: 'boom_flash.ogg',      // светошумовая
  smokePop: 'boom_smoke.ogg',   // хлопок вышибного заряда дымовой
  glass: 'glass_break.ogg',     // бутылка молотова о землю
  hiss: 'smoke_hiss.ogg',       // ЛУП: дымовая травит газ
  fire: 'fire_loop.ogg',        // ЛУП: горит лужа молотова
};
const fxBufs = {};

async function loadFxSamples() {
  await Promise.all(Object.keys(FX_FILES).map(async (name) => {
    const buf = await loadSample(FX_FILES[name]);
    if (buf) fxBufs[name] = buf;
  }));
}

/**
 * Одноразовый звук события в точке мира.
 *
 * `ref` — опорная дистанция затухания (как `REF_DIST` у звуков из `OP_SOUND`):
 * у взрыва она большая, у бьющейся бутылки маленькая. Возвращает `false`,
 * если семпл не загружен — зовущий играет синтез, как и везде.
 */
export function playFxAt(name, x, y, z, gain = 0.6, ref = 30) {
  if (!actx || !fxBufs[name]) return false;
  const out = spatialOut(x, y, z, ref, 0);
  if (out) playBuffer(fxBufs[name], gain, out.node, out.att);
  return true;
}

/** Тот же звук, но себе в колонки без затухания (свой бросок). */
export function playFx(name, gain = 0.6) {
  if (!actx || !fxBufs[name]) return false;
  playBuffer(fxBufs[name], gain, null);
  return true;
}

/**
 * Длящиеся звуки: шипение дымовой и горение молотова.
 *
 * ⚠️ Почему они устроены иначе, чем всё остальное в этом файле: обычный
 * звук здесь одноразовый — цепочка узлов собирается на момент запуска и
 * больше не трогается, потому что за 0.3 с игрок никуда не денется.
 * Дым травит газ 12 секунд, и за это время можно обойти его кругом —
 * значит громкость и панорама обязаны пересчитываться каждый кадр
 * (`updateAmbients` из main.js), иначе звук останется висеть слева,
 * когда источник уже справа.
 */
const ambients = [];

/** Больше этого одновременно не заводим: дымовая на дымовой — каша и нагрузка */
const MAX_AMBIENT = 10;

/**
 * Запустить длящийся звук в точке мира.
 *
 * `life` — сколько секунд он живёт (у дыма и огня это время задаёт тот, кто
 * их породил: `grenades.js`). Фолбэка синтезом у длящихся звуков нет: не
 * загрузился файл — просто тихо.
 */
export function startAmbient(name, x, y, z, { gain = 0.5, ref = 12, life = 10, fadeIn = 0.4, fadeOut = 2.0 } = {}) {
  if (!actx || !fxBufs[name] || ambients.length >= MAX_AMBIENT) return null;

  const src = actx.createBufferSource();
  src.buffer = fxBufs[name];
  src.loop = true;                                  // ⚠️ файл собран бесшовным
  src.playbackRate.value = 0.97 + Math.random() * 0.06;

  const g = actx.createGain();
  g.gain.value = 0;                                 // громкость выставит первый же кадр
  const flt = actx.createBiquadFilter();
  flt.type = 'lowpass';
  flt.frequency.value = 20000;
  const pan = actx.createStereoPanner ? actx.createStereoPanner() : null;

  src.connect(flt); flt.connect(g);
  if (pan) { g.connect(pan); pan.connect(actx.destination); } else g.connect(actx.destination);
  src.start();

  const a = { src, g, flt, pan, x, y, z, gain, ref, life, fadeIn, fadeOut, born: actx.currentTime };
  ambients.push(a);
  return a;
}

/**
 * Пересчитать все длящиеся звуки под текущее положение слушателя.
 * Зовётся каждый кадр из main.js.
 *
 * Значения ставятся через `setTargetAtTime`, а не присваиванием: при
 * присваивании каждый кадр резкие изменения (респавн, рывок reconciliation)
 * дают щелчки на границе кадров.
 */
export function updateAmbients() {
  if (!actx) return;
  const now = actx.currentTime;
  for (let i = ambients.length - 1; i >= 0; i--) {
    const a = ambients[i];
    const age = now - a.born;
    if (age >= a.life) {
      try { a.src.stop(); } catch { /* уже остановлен */ }
      ambients.splice(i, 1);
      continue;
    }
    const dx = a.x - player.pos.x, dy = a.y - player.pos.y, dz = a.z - player.pos.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const att = a.ref / (a.ref + dist);
    // Плавные концы: без fadeIn щёлкает старт, без fadeOut звук обрывается
    // на полуслове ровно тогда, когда дым ещё виден.
    const env = Math.min(1, age / a.fadeIn) * Math.min(1, (a.life - age) / a.fadeOut);
    a.g.gain.setTargetAtTime(a.gain * att * env, now, 0.08);
    a.flt.frequency.setTargetAtTime(700 + 19000 * att * att, now, 0.08);
    if (a.pan && dist > PAN_MIN_DIST) {
      const rx = Math.cos(S.yaw), rz = -Math.sin(S.yaw);
      a.pan.pan.setTargetAtTime(Math.max(-1, Math.min(1, (dx * rx + dz * rz) / dist)), now, 0.08);
    }
  }
}

// ============================================================
//  Звон в ушах после светошумовой (tinnitus)
//
//  Это НЕ звук в точке мира, а последствие для того, кого ослепило:
//  хлопок приходит из точки взрыва и затухает за полсекунды, а звон стоит
//  в ушах всё время белого экрана. Поэтому он играется прямо в колонки,
//  без затухания по дистанции и без панорамы, и его громкость ведёт
//  ослепление (`S.flashAmt`), а не таймер.
//
//  ⚠️ Устроен как длящиеся звуки (`ambients`), а не как одноразовые:
//  громкость обязана пересчитываться каждый кадр, иначе она не сможет
//  «гаснуть вместе со светом». Но список `ambients` ему не подходит —
//  там точка мира и жёсткая длительность `life`, а здесь ни того, ни
//  другого: конец наступает тогда, когда экран перестал быть белым.
// ============================================================

/** Громкость звона при полном ослеплении (level = 1) */
const TINNITUS_GAIN = 0.16;

/** Две частоты: «пи-и-и» одним тоном звучит как сигнал приборной панели */
const TINNITUS_HZ = [4200, 6300];

let tinnitus = null;

/**
 * Выставить громкость звона. Зовётся каждый кадр из `grenades.js`, уровень —
 * текущая белизна экрана `S.flashAmt` (0 = ослепления нет).
 *
 * Заводится сам при первом уровне > 0 и глушится сам при возврате к нулю,
 * поэтому отдельных start/stop нет: одна точка вызова — одно состояние.
 *
 * Громкость берётся как `level²` намеренно: в линейной зависимости хвост
 * ослепления (экран уже почти прозрачный, а спад идёт ещё секунд десять)
 * оставался отчётливо слышимым и превращался в раздражение вместо эффекта.
 */
export function setTinnitus(level) {
  if (!actx) return;
  const now = actx.currentTime;

  if (level <= 0.005) {
    if (!tinnitus) return;
    // Плавно гасим и только потом останавливаем: обрыв генератора на
    // ненулевой амплитуде даёт щелчок громче самого звона.
    tinnitus.g.gain.cancelScheduledValues(now);
    tinnitus.g.gain.setTargetAtTime(0, now, 0.12);
    for (const o of tinnitus.oscs) { try { o.stop(now + 0.6); } catch { /* уже остановлен */ } }
    tinnitus = null;
    return;
  }

  if (!tinnitus) {
    const g = actx.createGain();
    g.gain.setValueAtTime(0, now);
    g.connect(actx.destination);
    const oscs = TINNITUS_HZ.map((hz, i) => {
      const o = actx.createOscillator();
      o.type = 'sine';
      // Лёгкая расстройка на запуск: два флеша подряд не должны звучать
      // как один и тот же файл, включённый второй раз.
      o.frequency.value = hz * (0.98 + Math.random() * 0.04);
      const og = actx.createGain();
      og.gain.value = i === 0 ? 1 : 0.45;   // верхний тон только подкрашивает
      o.connect(og); og.connect(g);
      o.start();
      return o;
    });
    // Старт резкий (это удар по ушам, а не нарастание), но не мгновенный:
    // ступенька амплитуды слышна как щелчок.
    g.gain.linearRampToValueAtTime(TINNITUS_GAIN * level * level, now + 0.03);
    tinnitus = { g, oscs };
    return;
  }

  tinnitus.g.gain.setTargetAtTime(TINNITUS_GAIN * level * level, now, 0.15);
}

/**
 * Приём кадра OP_SOUND: `[op][u8 count]` + `[u8 kind][i16 x,y,z]` × count.
 *
 * В кадре нет id источника — так задумано (см. shared/src/sound.rs): звуки
 * анонимны, и связать их в трек конкретного игрока читерский клиент не может.
 */
onMessage((op, d) => {
  if (op !== OP.SOUND) return;
  const n = d.getUint8(1);
  for (let i = 0; i < n; i++) {
    const o = 2 + i * 7;
    playAt(
      d.getUint8(o),
      unpackPos(d.getInt16(o + 1, true)),
      unpackPos(d.getInt16(o + 3, true)),
      unpackPos(d.getInt16(o + 5, true)),
    );
  }
});
